import React from 'react'
import { 
  TrendingUp,
  Ship,
  CheckCircle,
  Info
} from 'lucide-react'
import { useOnboardingStore } from '../../../stores/onboardingStore'
import { getAllIndustries } from '../industry-configs'
import { IndustryType } from '../../../types'
import { ScalableIndustryGrid } from '../components/ScalableIndustryGrid'
import { CustomIndustryRequestData } from '../components/CustomIndustryRequestModal'
import { HelpTooltip } from '../../../components/ui/Tooltip'

export const IndustrySelectionScreen: React.FC = () => {
  const { data, setSelectedIndustry } = useOnboardingStore()
  const industries = getAllIndustries()

  const handleIndustrySelect = (industry: IndustryType) => {
    setSelectedIndustry(industry)
  }

  // Custom industry request handler - uses communication service
  const handleCustomIndustryRequest = async (requestData: CustomIndustryRequestData) => {
    // The CustomIndustryRequestModal already has the communication service implementation
    // We don't need to handle it here - the modal's onSubmit prop handles it
    // This function is just a placeholder that gets passed to the modal
    console.log('Custom Industry Request Submitted:', requestData)
  }

  return (
    <div className="space-y-8">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
          Select Your Industry
          <HelpTooltip text="Choose the industry that best matches your primary business operations. This selection determines the types of intelligence and monitoring you'll receive." />
        </h2>
        <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
          Each industry faces unique market pressures and opportunities. Choose your industry to receive 
          tailored intelligence monitoring that matters to your specific business situation.
        </p>
        
        {/* Additional help info */}
        <div className="mt-4 inline-flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-4 py-2 rounded-full">
          <Info className="w-4 h-4" />
          <span>Can't find your industry? Use the "Request Custom Industry" option below</span>
        </div>
      </div>

      {/* Scalable Industry Grid with Custom Request Integration */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Available Industries</h3>
          <HelpTooltip text="We've pre-configured intelligence monitoring for these industries. Each includes specific market indicators, supplier categories, and competitive factors relevant to that sector." />
        </div>
        
        <ScalableIndustryGrid
          industries={industries}
          selectedIndustry={data.selectedIndustry}
          onIndustrySelect={handleIndustrySelect}
          onCustomIndustryRequest={handleCustomIndustryRequest}
          initialDisplayCount={6}
        />
      </div>

      {/* Intelligence Preview Section */}
      {data.selectedIndustry && (
        <div className="mt-12 p-8 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <div className="text-xl font-bold text-green-800 mb-3 flex items-center gap-2">
                Intelligence Preview for {industries.find(i => i.id === data.selectedIndustry)?.name}
                <HelpTooltip text="This preview shows the types of intelligence monitoring that will be configured for your selected industry. You can customize these further in the next steps." />
              </div>
              <div className="text-green-700 mb-6">
                Your customized intelligence monitoring will include advanced insights across these key areas:
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-200 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-green-700" />
                    </div>
                    <h4 className="font-semibold text-green-800 flex items-center gap-2">
                      Revenue Intelligence
                      <HelpTooltip text="Track and analyze revenue streams specific to your industry, including seasonal patterns, market trends, and growth opportunities." />
                    </h4>
                  </div>
                  <ul className="space-y-2 ml-11">
                    {industries.find(i => i.id === data.selectedIndustry)?.revenueStreamTypes.slice(0, 2).map((stream, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-green-700">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                        <span><strong>{stream.name}</strong> monitoring and trend analysis</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-200 rounded-lg flex items-center justify-center">
                      <Ship className="w-4 h-4 text-green-700" />
                    </div>
                    <h4 className="font-semibold text-green-800 flex items-center gap-2">
                      Supply Chain Intelligence
                      <HelpTooltip text="Monitor supplier relationships, price fluctuations, and supply chain risks relevant to your industry operations." />
                    </h4>
                  </div>
                  <ul className="space-y-2 ml-11">
                    {industries.find(i => i.id === data.selectedIndustry)?.supplierCategories.slice(0, 2).map((category, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-green-700">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                        <span><strong>{category.name}</strong> tracking and optimization</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-white/70 rounded-xl border border-green-200/50">
                <div className="flex items-center gap-2 text-sm text-green-700 font-medium">
                  <CheckCircle className="w-4 h-4" />
                  Ready to proceed? Click "Continue Setup" to begin your customized intelligence configuration
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}